/**
 * Document bottleneck queue drilldowns.
 */
import { formatInteger } from "./format.js";
import { escapeAttr, escapeHtml, labelize } from "./dashboard-html.js";
import { taskAssignmentFormHtml } from "./dashboard-task-form.js";
import type {
  LeadFactoryDocumentThroughputDocument,
  LeadFactoryDocumentThroughputQueueResponse,
  LeadFactoryDocumentThroughputQueues,
  LeadFactoryTask
} from "./dashboard-lead-factory-types.js";
import {
  leadFactoryDocumentClasses,
  leadFactoryManualFactFields,
  leadFactoryManualFactValueTypes,
  leadFactoryReviewOutcomes
} from "./dashboard-lead-factory-constants.js";

export function documentThroughputQueuesHtml(queues: LeadFactoryDocumentThroughputQueues): string {
  return `
    <section class="document-throughput-queues" data-document-throughput-queue>
      <div class="panel-heading">
        <h2>Document Bottleneck Queues</h2>
        <span class="subtle">classification, extraction, OCR, and review drilldowns</span>
      </div>
      <div class="document-throughput-queue-grid">
        ${documentThroughputQueueLaneHtml(queues.classification)}
        ${documentThroughputQueueLaneHtml(queues.extraction)}
        ${documentThroughputQueueLaneHtml(queues.ocr)}
        ${documentThroughputQueueLaneHtml(queues.review)}
      </div>
    </section>
  `;
}

export function documentThroughputQueueLaneHtml(queue: LeadFactoryDocumentThroughputQueueResponse): string {
  return `
    <div class="document-throughput-queue-lane" data-document-throughput-queue-stage="${escapeAttr(queue.stage)}">
      <div class="mini-heading">
        <strong>${escapeHtml(queue.label)}</strong>
        <span>${formatInteger(queue.summary.loaded)} loaded · ${formatInteger(queue.summary.withActiveTask)} tasked</span>
      </div>
      <div class="document-throughput-queue-list">
        ${queue.documents.map((document) => documentThroughputQueueRowHtml(document)).join("") || `<p class="empty">No ${escapeHtml(queue.label.toLowerCase())} documents loaded.</p>`}
      </div>
    </div>
  `;
}

export function documentThroughputQueueRowHtml(document: LeadFactoryDocumentThroughputDocument): string {
  const title = document.property_address || document.owner_name || document.property_lead_id || document.id;
  const status = `${labelize(document.classification_status)} · ${labelize(document.extraction_status)}`;
  return `
    <article class="document-throughput-queue-row" data-document-throughput-queue-row data-document-throughput-source="${escapeAttr(document.id)}">
      <div>
        <strong>${escapeHtml(title)}</strong>
        <small>${escapeHtml(document.county)} · ${escapeHtml(document.sourceSummary || document.id)}</small>
        <span>${escapeHtml(status)}</span>
      </div>
      <div class="document-throughput-queue-meta">
        <span>${formatInteger(document.active_task_count)} tasks</span>
        <span>${formatInteger(document.fact_count)} facts</span>
        <span>${formatInteger(document.ocr_result_count)} OCR</span>
      </div>
      ${documentThroughputStageOutputHtml(document)}
      ${documentThroughputTaskControlsHtml(document)}
      <div class="lead-generation-card-actions">
        ${document.property_lead_id ? `<button class="button secondary" type="button" data-lead-action="detail" data-lead-id="${escapeAttr(document.property_lead_id)}">Lead</button>` : ""}
        <button class="button secondary" type="button" data-source-document-review="${escapeAttr(document.id)}" data-document-throughput-review-action="${escapeAttr(document.stage)}">Review PDF</button>
      </div>
    </article>
  `;
}

export function documentThroughputStageOutputHtml(document: LeadFactoryDocumentThroughputDocument): string {
  if (document.stage === "classification") return documentThroughputClassificationFormHtml(document);
  if (document.stage === "extraction") return documentThroughputManualFactFormHtml(document);
  if (document.stage === "ocr") return documentThroughputOcrResultFormHtml(document);
  if (document.stage === "review" && document.task_id) return documentThroughputReviewOutcomeFormHtml(document);
  return "";
}

export function documentThroughputClassificationFormHtml(document: LeadFactoryDocumentThroughputDocument): string {
  const currentClass = leadFactoryDocumentClasses.includes(document.source_document_type as any) ? document.source_document_type : "unknown";
  return `
    <form class="document-throughput-output-form" data-document-throughput-classification-form data-source-document-id="${escapeAttr(document.id)}">
      <select aria-label="Document class" data-document-throughput-classification-class>
        ${leadFactoryDocumentClasses
          .map((documentClass) => `<option value="${escapeAttr(documentClass)}" ${documentClass === currentClass ? "selected" : ""}>${escapeHtml(labelize(documentClass))}</option>`)
          .join("")}
      </select>
      <button class="button secondary" type="submit" data-document-throughput-classification-action="${escapeAttr(document.id)}">Classify</button>
    </form>
  `;
}

export function documentThroughputManualFactFormHtml(document: LeadFactoryDocumentThroughputDocument): string {
  return `
    <form class="document-throughput-output-form document-throughput-fact-form" data-document-throughput-manual-fact-form data-source-document-id="${escapeAttr(document.id)}">
      <select aria-label="Fact field" data-document-throughput-manual-fact-field>
        ${leadFactoryManualFactFields
          .map((field) => `<option value="${escapeAttr(field)}" data-value-type="${escapeAttr(leadFactoryManualFactValueTypes[field])}">${escapeHtml(labelize(field))}</option>`)
          .join("")}
      </select>
      <input type="text" maxlength="240" aria-label="Fact value" placeholder="Value from PDF" data-document-throughput-manual-fact-value>
      <input type="text" maxlength="120" aria-label="Normalized value" placeholder="Normalized" data-document-throughput-manual-fact-normalized>
      <input type="number" min="1" max="9999" step="1" aria-label="Source page" placeholder="Page" data-document-throughput-manual-fact-source-page>
      <input type="text" maxlength="240" aria-label="Evidence excerpt" placeholder="Evidence excerpt" data-document-throughput-manual-fact-evidence-excerpt>
      <button class="button secondary" type="button" data-document-throughput-manual-fact-page-focus="${escapeAttr(document.id)}">Review Page</button>
      <button class="button secondary" type="submit" data-document-throughput-manual-fact-action="${escapeAttr(document.id)}">Capture</button>
    </form>
  `;
}

export function documentThroughputOcrResultFormHtml(document: LeadFactoryDocumentThroughputDocument): string {
  return `
    <form class="document-throughput-output-form document-throughput-ocr-form" data-document-throughput-ocr-result-form data-source-document-id="${escapeAttr(document.id)}">
      <textarea rows="3" maxlength="4000" aria-label="OCR text" placeholder="OCR text" data-document-throughput-ocr-result-text></textarea>
      <button class="button secondary" type="submit" data-document-throughput-ocr-result-action="${escapeAttr(document.id)}">Ingest OCR</button>
    </form>
  `;
}

export function documentThroughputReviewOutcomeFormHtml(document: LeadFactoryDocumentThroughputDocument): string {
  return `
    <form class="document-throughput-output-form document-throughput-review-form" data-document-throughput-review-outcome-form data-source-document-id="${escapeAttr(document.id)}" data-task-id="${escapeAttr(document.task_id || "")}">
      <select aria-label="Review outcome" data-document-throughput-review-outcome>
        ${leadFactoryReviewOutcomes
          .map((outcome) => `<option value="${escapeAttr(outcome)}">${escapeHtml(labelize(outcome))}</option>`)
          .join("")}
      </select>
      <input type="text" maxlength="120" value="evidence_confirmed" aria-label="Review reason" data-document-throughput-review-reason>
      <label class="source-review-selector">
        <input type="checkbox" data-document-throughput-review-confirm>
        <span>Confirm</span>
      </label>
      <button class="button secondary" type="submit" data-document-throughput-review-outcome-action="${escapeAttr(document.task_id || "")}">Record</button>
    </form>
  `;
}

export function documentThroughputTaskControlsHtml(document: LeadFactoryDocumentThroughputDocument): string {
  if (!document.task_id) {
    return `<p class="document-throughput-task-empty">No active ${escapeHtml(document.stageLabel.toLowerCase())} task is linked.</p>`;
  }
  const task: LeadFactoryTask = {
    id: document.task_id,
    property_lead_id: document.property_lead_id ?? null,
    source_document_id: document.id,
    task_type: document.task_type || document.stage,
    status: document.task_status || "open",
    priority: "normal",
    assigned_to: document.task_assigned_to ?? null,
    due_at: document.task_due_at ?? null,
    source_operator: "lead_factory",
    blocking_reason: document.task_blocking_reason ?? null,
    instructions: document.actionLabel,
    created_at: document.created_at
  };
  const taskSummary = [
    document.actionLabel,
    labelize(task.status),
    task.assigned_to ? `assigned ${task.assigned_to}` : "unassigned",
    task.due_at ? `due ${task.due_at}` : "",
    task.blocking_reason ? `blocked: ${task.blocking_reason}` : ""
  ].filter(Boolean).join(" · ");
  return `
    <div class="document-throughput-task-controls" data-document-throughput-task="${escapeAttr(task.id)}">
      <small data-document-throughput-task-summary>${escapeHtml(taskSummary)}</small>
      <div class="document-throughput-task-actions">
        ${taskAssignmentFormHtml(task)}
        <button class="button secondary" type="button" data-task-complete="${escapeAttr(task.id)}" data-document-throughput-task-complete="${escapeAttr(task.id)}">Complete</button>
      </div>
    </div>
  `;
}

